/* eslint-disable react/no-danger */
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { _ } from '_locales';

import { cts_secure } from '_cts';
import apis from '_apis';
import { colors } from '_styles';
import oneOf from '../DepoEvents_Components/helpers';
import { Contact } from '../DepoEvents_Components';
import {
  DetailsComponent,
  LocationInformationComponent,
} from './component';
import { LoadingSpinner } from '../../../components';

/**
 * import styles
 */

const LocationContainer = styled.div`
  text-align: left !important;
`;

const Text = styled.h4`
  font-size: 16px !important;
  margin: 0;
  margin-bottom: 20px;
  text-align: left !important;
`;

const DetailsContainer = styled.div`
  position: relative;
  padding: 20px;
  text-align: left !important;
`;

const Title = styled.h2`
  font-size: 16px !important;
  margin-bottom: 5px;
  margin-top: 20px;
  text-align: left !important;
`;

const AddressLink = styled.a`
  display: inline-block;
  margin-top: 10px;
  margin-bottom: 25px;
  font-size: 16px !important;
  font-weight: 500 !important;
  text-transform: capitalize;
  text-align: left !important;
  color: ${colors.RC_ACTION} !important;
`;

// End of styles

const endpoints = ({
  projectId, districtId, matId, lang, localityId
}) => ({
  'event-details': apis.getEventDetailsApi(localityId, matId, lang),
  'depo-details': apis.getDepoDetailsApi(localityId, matId, lang),
});

/**
 * Get details hook
 * @author Alireza Zebardast
 * @param {number} matId
 * @param {string} lang
 * @param {string} kind
 * @param {string} projectId
 * @param {string} districtId
 */
const useDepoEventDetails = (projectId, districtId, matId, kind, lang, localityId) => {
  const [details, setDetails] = useState({});
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const getDepoEvents = async () => {
      try {
        setLoading(true);
        const { data, status } = await cts_secure('get', endpoints({
          projectId, districtId, matId, lang, localityId
        })[kind]);

        if (
          !data
          && status !== 200
        ) throw new Error('Error getting depot details');

        setDetails(oneOf(['eventDetails', 'depotDetails'], data.content.response));

        return undefined;
      } catch (e) {
        setError(true);
        return Promise.reject(e.message);
      } finally {
        setLoading(false);
      }
    };
    getDepoEvents();
  }, [matId, kind, projectId, districtId, lang, localityId]);
  return {
    details,
    error,
    loading
  };
};

/**
 * This component is for preventing the details of drop of locations or events
 *
 * @author Alireza Zebardast
 * @param {object} plugin
 * @param {object} config
 * @param {object} props
 */
const DepoEventDetails = ({ plugin, config, props }) => {
  const { projectId } = plugin;
  const { selectedLang, districtId, localityId } = config;

  const { location } = props;

  const [activeOption, setActiveOption] = useState('');

  const { id, depoEventKind } = location.state; /* Depo details or Event details */

  const { loading, error, details } = useDepoEventDetails(
    projectId, districtId, id, depoEventKind, selectedLang, localityId
  );

  const depoEventName = oneOf(['event_nm', 'depot_nm'], details);
  const depoEventDetails = oneOf(['event_details', 'depot_details'], details);

  const {
    geo_lat,
    geo_lng,
    display_addr,
    phone_nbr,
    email_addr,
    web_addr,
    season
  } = details;

  useEffect(() => {
    if (
      (
        phone_nbr !== 'null'
        && phone_nbr
      )
      || (
        email_addr !== 'null'
        && email_addr
      )
      || (
        web_addr !== 'null'
        && web_addr
      )
      || (
        season
        && season.length
      )
    ) return setActiveOption('location');

    if (
      depoEventDetails
      && depoEventDetails !== 'null'
    ) return setActiveOption('details');

    return undefined;
  }, [phone_nbr, email_addr, web_addr, season, depoEventDetails]);

  return (
    <>
      {
        loading
          ? (<LoadingSpinner height={500} />)
          : (
            <DetailsContainer>
              <LocationContainer>
                {
                  !error
                    && details
                    && Object.keys(details).length
                    ? (
                      <Text>{depoEventName}</Text>
                    ) : null
                }
              </LocationContainer>

              {
                !error
                  && details
                  && Object.keys(details).length
                  ? (
                    <DetailsComponent
                      details={details}
                      selectedLang={selectedLang}
                      depoEventDetails={depoEventDetails}
                    />
                  ) : null
              }
              <Title>
                {_('zone.address')}
              </Title>

              {
                !error
                  && details
                  && Object.keys(details).length
                  ? (
                    <div>
                      {
                        (display_addr
                          && !display_addr.includes('null'))
                          ? (
                            <Contact address={display_addr} />
                          ) : null
                      }

                      <AddressLink target="_blank" href={`https://maps.google.com/?q=${geo_lat},${geo_lng}&z=8`}>
                        view on map
                      </AddressLink>

                      <LocationInformationComponent
                        season={season}
                        webAdr={web_addr}
                        phoneNbr={phone_nbr}
                        emailAdr={email_addr}
                      />
                    </div>

                  ) : null
              }

            </DetailsContainer>
          )
      }
    </>
  );
};

export default DepoEventDetails;
